/**
 * Name Filter Definition
 *
 * Filters nodes by their name (multi-language aware with phonetic matching)
 * Uses the same phonetic search utilities as the main search functionality.
 */

import { GraphDataNode } from '../../types/graph.types';
import {
  FilterDefinition,
  FilterOperator,
  FilterValueType,
  FilterSuggestion,
  FilterValue,
} from '../types';
import { quickSearch } from '../../utils/search';

/**
 * Remove diacritics from a string for comparison (e.g., 'śrī' -> 'sri')
 */
function removeDiacritics(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

/**
 * The name fields a node actually carries.
 *
 * The catalog ships exactly two, in every one of the ten languages:
 *
 *   data.name            native script — Telugu/Kannada/… when l != en
 *   data.normalizedName  ASCII romanization — IDENTICAL across languages
 *
 * `normalizedName` exists specifically so a latin query works in a non-English
 * catalog: jnana-serverless graphUtil.ts injects it from `enNormalizedName`
 * ("so users can search in normalized english characters across all
 * languages") and then deletes every per-language field, so `enNormalizedName`,
 * `enName` and `teName` never reach us. Matching must therefore consider BOTH
 * fields — `name` alone is native script and rejects every latin query outside
 * English; `normalizedName` alone rejects a native-script query.
 */
function nameField(node: GraphDataNode, key: 'name' | 'normalizedName'): string {
  // node.data is Record<string, unknown> in the new typing — coerce, default ''.
  const value = (node.data as Record<string, unknown> | undefined)?.[key];
  return typeof value === 'string' ? value : '';
}

function getNodeSearchableNames(node: GraphDataNode): string[] {
  return [nameField(node, 'normalizedName'), nameField(node, 'name')].filter(Boolean);
}

/**
 * The name to quote back at the user in a suggestion.
 *
 * Deliberately NOT the field that matched: a Telugu reader searching "hanuman"
 * matched on the romanization but is looking at native script on the browse
 * cards, so that is what the dropdown shows. Matching on the romanization and
 * displaying it too would put raw slugs ("vira hanuman vira hanuman  telugu ")
 * in the English dropdown, where the diacritic name renders today.
 *
 * Mirrors nodeName()'s preference for non-English, falling back to the
 * romanization for the few node types (album, track) that ship no `name`.
 */
function getNodeDisplayName(node: GraphDataNode): string {
  return nameField(node, 'name') || nameField(node, 'normalizedName');
}

/**
 * Name Filter Definition
 */
export const nameFilter: FilterDefinition = {
  id: 'name',
  label: 'Name',
  shortLabel: 'Name',
  icon: 'DocumentTextIcon',
  valueType: FilterValueType.String,
  supportedOperators: [
    FilterOperator.SoundsLike,
    FilterOperator.Contains,
    FilterOperator.StartsWith,
    FilterOperator.EndsWith,
    FilterOperator.Is,
    FilterOperator.NotContains,
  ],
  defaultOperator: FilterOperator.SoundsLike,
  placeholder: 'Enter name to search...',

  suggestMatch(query: string, nodes: GraphDataNode[]): FilterSuggestion[] {
    const trimmedQuery = query.trim();
    if (!trimmedQuery || trimmedQuery.length < 2) {
      return [];
    }

    // Skip if query is purely numeric (let nodeIdFilter handle it)
    if (/^\d+$/.test(trimmedQuery)) {
      return [];
    }

    const normalizedQuery = removeDiacritics(trimmedQuery);
    // Use lowercase for the filter value to ensure case-insensitive matching
    const lowercaseQuery = trimmedQuery.toLowerCase();
    const suggestions: FilterSuggestion[] = [];

    // Count matches for different operators (text-based and phonetic)
    let containsCount = 0;
    let startsWithCount = 0;
    let exactCount = 0;
    let phoneticOnlyCount = 0;

    // Sample matching nodes for descriptions
    const containsMatches: string[] = [];
    const startsWithMatches: string[] = [];
    const phoneticMatches: string[] = [];

    // Track which nodes matched via text vs phonetic
    const textMatchedIds = new Set<number>();

    for (const node of nodes) {
      // Match across every name field, but count each node at most once per
      // operator — a bhajan whose native and romanized names both contain the
      // query is still one match.
      const names = getNodeSearchableNames(node).map(removeDiacritics);
      if (!names.length) continue;

      const display = getNodeDisplayName(node);
      let matchedByText = false;

      if (names.some((n) => n === normalizedQuery)) {
        exactCount++;
        matchedByText = true;
      }

      if (names.some((n) => n.startsWith(normalizedQuery))) {
        startsWithCount++;
        if (startsWithMatches.length < 3) {
          startsWithMatches.push(display);
        }
        matchedByText = true;
      }

      if (names.some((n) => n.includes(normalizedQuery))) {
        containsCount++;
        if (containsMatches.length < 3) {
          containsMatches.push(display);
        }
        matchedByText = true;
      }

      if (matchedByText) {
        textMatchedIds.add(node.nodeId);
      }
    }

    // Second pass: find phonetic-only matches using the proper search utility
    const phoneticResults = quickSearch(
      nodes.filter(n => !textMatchedIds.has(n.nodeId)),
      trimmedQuery
    );

    for (const node of phoneticResults) {
      const display = getNodeDisplayName(node);
      if (!display) continue;

      phoneticOnlyCount++;
      if (phoneticMatches.length < 3) {
        phoneticMatches.push(display);
      }
    }

    // Add "sounds like" suggestion first (highest priority)
    // Always show if query is long enough, as phonetic search is the most flexible
    if (trimmedQuery.length >= 3) {
      const examples = phoneticMatches.slice(0, 2).join(', ');
      const totalPhonetic = containsCount + phoneticOnlyCount;
      suggestions.push({
        filterId: 'name',
        label: `Name: sounds like "${trimmedQuery}"`,
        description: totalPhonetic > 0
          ? `${totalPhonetic} match${totalPhonetic !== 1 ? 'es' : ''}: ${examples}${totalPhonetic > 2 ? '...' : ''}`
          : 'Search by pronunciation',
        prefilledOperator: FilterOperator.SoundsLike,
        prefilledValue: lowercaseQuery,
        needsConfiguration: false,
        score: 100, // Highest priority
      });
    }

    // Count ends with matches
    let endsWithCount = 0;
    const endsWithMatches: string[] = [];
    for (const node of nodes) {
      const names = getNodeSearchableNames(node).map(removeDiacritics);
      if (!names.length) continue;
      if (names.some((n) => n.endsWith(normalizedQuery))) {
        endsWithCount++;
        if (endsWithMatches.length < 3) {
          endsWithMatches.push(getNodeDisplayName(node));
        }
      }
    }

    // Add "contains" suggestion (second priority) - always show
    {
      const examples = containsMatches.slice(0, 2).join(', ');
      suggestions.push({
        filterId: 'name',
        label: `Name: contains "${trimmedQuery}"`,
        description: containsCount > 0
          ? `${containsCount} match${containsCount !== 1 ? 'es' : ''}: ${examples}${containsCount > 2 ? '...' : ''}`
          : 'Search for names containing this text',
        prefilledOperator: FilterOperator.Contains,
        prefilledValue: lowercaseQuery,
        needsConfiguration: false,
        score: 90, // Second priority
      });
    }

    // Add "starts with" suggestion (third priority) - always show
    {
      const examples = startsWithMatches.slice(0, 2).join(', ');
      suggestions.push({
        filterId: 'name',
        label: `Name: starts with "${trimmedQuery}"`,
        description: startsWithCount > 0
          ? `${startsWithCount} match${startsWithCount !== 1 ? 'es' : ''}: ${examples}${startsWithCount > 2 ? '...' : ''}`
          : 'Search for names starting with this text',
        prefilledOperator: FilterOperator.StartsWith,
        prefilledValue: lowercaseQuery,
        needsConfiguration: false,
        score: 80, // Third priority
      });
    }

    // Add "ends with" suggestion (fourth priority) - always show
    {
      const examples = endsWithMatches.slice(0, 2).join(', ');
      suggestions.push({
        filterId: 'name',
        label: `Name: ends with "${trimmedQuery}"`,
        description: endsWithCount > 0
          ? `${endsWithCount} match${endsWithCount !== 1 ? 'es' : ''}: ${examples}${endsWithCount > 2 ? '...' : ''}`
          : 'Search for names ending with this text',
        prefilledOperator: FilterOperator.EndsWith,
        prefilledValue: lowercaseQuery,
        needsConfiguration: false,
        score: 70, // Fourth priority
      });
    }

    // Add "is exactly" suggestion (fifth priority) - always show
    suggestions.push({
      filterId: 'name',
      label: `Name: is exactly "${trimmedQuery}"`,
      description: exactCount > 0
        ? `${exactCount} exact match${exactCount !== 1 ? 'es' : ''}`
        : 'Search for exact name match',
      prefilledOperator: FilterOperator.Is,
      prefilledValue: lowercaseQuery,
      needsConfiguration: false,
      score: 60, // Fifth priority
    });

    // Add "does not contain" suggestion (sixth priority) - always show
    suggestions.push({
      filterId: 'name',
      label: `Name: does not contain "${trimmedQuery}"`,
      description: 'Exclude names containing this text',
      prefilledOperator: FilterOperator.NotContains,
      prefilledValue: lowercaseQuery,
      needsConfiguration: false,
      score: 50, // Sixth priority
    });

    return suggestions.sort((a, b) => b.score - a.score);
  },

  execute(node: GraphDataNode, operator: FilterOperator, value: FilterValue): boolean {
    const filterValue = String(value).toLowerCase();
    if (!filterValue) {
      return true;
    }

    // normalizedName (ASCII, present in every language) AND name (the reader's
    // script) are both checked, so "hanuman" and "హనుమాన్" each find the same
    // bhajan. Checking `name` alone — which is what the pre-slim-index field
    // names silently degraded to — returned 0 results for a latin query in all
    // nine non-English languages, and inverted NotContains into a filter that
    // matched the entire catalog. See getNodeSearchableNames.
    const namesToCheck = getNodeSearchableNames(node);

    if (namesToCheck.length === 0) {
      return false;
    }

    // Normalize filter value
    const normalizedFilter = removeDiacritics(filterValue);

    // For SoundsLike, use the proper phonetic search from search.ts
    if (operator === FilterOperator.SoundsLike) {
      // Use quickSearch which handles phonetic matching properly
      const matches = quickSearch([node], filterValue);
      return matches.length > 0;
    }

    // For text-based operators, check each name field
    for (const name of namesToCheck) {
      const normalizedName = removeDiacritics(name);

      switch (operator) {
        case FilterOperator.Contains:
          if (normalizedName.includes(normalizedFilter)) {
            return true;
          }
          break;

        case FilterOperator.Is:
          if (normalizedName === normalizedFilter) {
            return true;
          }
          break;

        case FilterOperator.StartsWith:
          if (normalizedName.startsWith(normalizedFilter)) {
            return true;
          }
          break;

        case FilterOperator.EndsWith:
          if (normalizedName.endsWith(normalizedFilter)) {
            return true;
          }
          break;

        case FilterOperator.NotContains:
          // For "not contains", ALL name fields must not contain the value
          // So we check the opposite and return early if found
          if (normalizedName.includes(normalizedFilter)) {
            return false;
          }
          break;

        default:
          return true;
      }
    }

    // For "not contains", if we get here, none of the fields contained the value
    if (operator === FilterOperator.NotContains) {
      return true;
    }

    return false;
  },

  getDisplayValue(value: FilterValue): string {
    const strValue = String(value);
    // Truncate long values for display
    if (strValue.length > 20) {
      return `${strValue.substring(0, 17)}...`;
    }
    return strValue;
  },
};
